
import { useLayoutEffect, useState, type ReactNode, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { Search } from 'lucide-react';

type InlineSearchDropdownProps = {
  open: boolean;
  anchorRef: RefObject<HTMLElement | null>;
  dropdownRef: RefObject<HTMLDivElement | null>;
  query: string;
  totalCount: number;
  visibleCount: number;
  children: ReactNode;
  emptyTitle?: string;
  emptyDescription?: string;
};

export function InlineSearchDropdown({
  open,
  anchorRef,
  dropdownRef,
  query,
  totalCount,
  visibleCount,
  children,
  emptyTitle = 'Nenhum resultado encontrado',
  emptyDescription = 'Tente buscar por outros termos',
}: InlineSearchDropdownProps) {
  const [dropdownRect, setDropdownRect] = useState<{
    top: number;
    left: number;
    width: number;
  } | null>(null);

  useLayoutEffect(() => {
    if (!open) {
      setDropdownRect(null);
      return;
    }
    const el = anchorRef.current;
    if (!el) return;

    const update = () => {
      const r = el.getBoundingClientRect();
      setDropdownRect({
        top: r.bottom + 8,
        left: r.left,
        width: r.width,
      });
    };

    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      ro.disconnect();
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [open, anchorRef]);

  if (!open || !dropdownRect || typeof document === 'undefined') return null;

  return createPortal(
    <div
      ref={dropdownRef as React.RefObject<HTMLDivElement>}
      role="listbox"
      className="fixed z-[10050] flex max-h-[min(70vh,520px)] flex-col overflow-hidden rounded-2xl border border-blue-100 bg-white shadow-2xl shadow-blue-900/25"
      style={{
        top: dropdownRect.top,
        left: dropdownRect.left,
        width: dropdownRect.width,
      }}
    >
      {visibleCount === 0 ? (
        <div className="px-8 py-10 text-center">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-gray-100">
            <Search className="h-7 w-7 text-gray-400" />
          </div>
          <p className="text-base font-semibold text-gray-700">{emptyTitle}</p>
          <p className="mt-1.5 text-sm text-gray-400">{emptyDescription}</p>
        </div>
      ) : (
        <>
          <div className="border-b border-blue-50 px-6 pb-3 pt-4">
            <span className="text-xs font-semibold uppercase tracking-wider text-gray-400">
              {totalCount} resultado{totalCount !== 1 ? 's' : ''} para &ldquo;{query}&rdquo;
            </span>
          </div>
          <div className="max-h-80 flex-1 overflow-y-auto">{children}</div>
          {totalCount > visibleCount ? (
            <div className="border-t border-gray-100 bg-gray-50 px-6 py-2.5 text-center">
              <span className="text-xs font-medium text-gray-400">
                Mostrando {visibleCount} de {totalCount} resultados
              </span>
            </div>
          ) : null}
        </>
      )}

      <div className="flex shrink-0 items-center gap-5 border-t border-gray-100 bg-gray-50 px-5 py-3">
        <div className="flex items-center gap-1.5 text-xs text-gray-400">
          <kbd className="inline-flex items-center rounded border border-gray-200 bg-white px-1.5 py-0.5 text-xs font-medium text-gray-500">
            ↑↓
          </kbd>
          <span>navegar</span>
        </div>
        <div className="flex items-center gap-1.5 text-xs text-gray-400">
          <kbd className="inline-flex items-center rounded border border-gray-200 bg-white px-1.5 py-0.5 text-xs font-medium text-gray-500">
            ↵
          </kbd>
          <span>selecionar</span>
        </div>
        <div className="flex items-center gap-1.5 text-xs text-gray-400">
          <kbd className="inline-flex items-center rounded border border-gray-200 bg-white px-1.5 py-0.5 text-xs font-medium text-gray-500">
            ESC
          </kbd>
          <span>fechar</span>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default InlineSearchDropdown;
